import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Container, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Link, 
  Alert, 
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Definindo o estilo do Paper diretamente como um objeto
const paperStyles = {
  padding: 4,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
  borderRadius: '12px'
};

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  
  const { login, resetPassword } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Por favor, preencha todos os campos');
      return;
    }
    
    try {
      setError('');
      setLoading(true);
      await login(email, password);
      navigate('/dashboard');
    } catch (err: any) {
      let errorMessage = 'Falha ao fazer login. Verifique suas credenciais.';
      
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        errorMessage = 'Email ou senha incorretos';
      } else if (err.code === 'auth/too-many-requests') {
        errorMessage = 'Muitas tentativas de login. Tente novamente mais tarde ou redefina sua senha.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetEmail) {
      setError('Por favor, insira seu email');
      return;
    }
    
    try {
      setError('');
      setLoading(true);
      await resetPassword(resetEmail);
      setResetSuccess(true);
    } catch (err: any) {
      let errorMessage = 'Falha ao enviar email de recuperação';
      
      if (err.code === 'auth/user-not-found') {
        errorMessage = 'Não existe conta com este email';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'Email inválido';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseResetDialog = () => {
    setResetPasswordOpen(false);
    setResetEmail('');
    setResetSuccess(false);
    setError('');
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <img 
            src="/images/logo-field-service-full.png"  
            alt="ACTField Logo" 
            style={{ 
              maxWidth: '280px', 
              height: 'auto',
              marginBottom: '16px'
            }} 
          />
        </Box>
        
        <Paper sx={paperStyles}>
          <Typography component="h2" variant="h5" sx={{ mb: 3 }}>
            Login
          </Typography>
          
          {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}
          
          <Box component="form" onSubmit={handleLogin} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Senha"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 3 }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ 
                mt: 2, 
                mb: 2, 
                py: 1.5,
                backgroundColor: '#2e7d32',
                '&:hover': {
                  backgroundColor: '#1b5e20',
                }
              }}
            >
              {loading ? <CircularProgress size={24} /> : 'Entrar'}
            </Button>
            
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Link 
                component="button" 
                variant="body2" 
                onClick={() => setResetPasswordOpen(true)}
                sx={{ cursor: 'pointer' }}
              >
                Esqueceu sua senha?
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
      
      {/* Password Reset Dialog */}
      <Dialog open={resetPasswordOpen} onClose={handleCloseResetDialog}>
        <DialogTitle>Recuperar Senha</DialogTitle>
        <DialogContent>
          {resetSuccess ? (
            <DialogContentText>
              Email de recuperação enviado com sucesso! Verifique sua caixa de entrada.
            </DialogContentText>
          ) : (
            <>
              <DialogContentText>
                Digite seu email para receber um link de recuperação de senha.
              </DialogContentText>
              <TextField
                autoFocus
                margin="dense"
                id="resetEmail"
                label="Email"
                type="email"
                fullWidth
                variant="outlined"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
              />
              {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResetDialog}>Fechar</Button>
          {!resetSuccess && (
            <Button 
              onClick={handleResetPassword} 
              color="primary" 
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Enviar'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Login;
